import useAsync from 'react-use/lib/useAsync';

import {
  CompoundEntityRef,
  Entity,
  parseEntityRef,
} from '@backstage/catalog-model';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

const SOURCE_TEMPLATE_ANNOTATION = 'backstage.io/source-template';

/**
 * Returns the compound entity ref of the source template that was used to
 * create this entity (assuming that it still exists and the user has access
 * to it).
 */
export const useSourceTemplateCompoundEntityRef = (entity: Entity) => {
  const catalogApi = useApi(catalogApiRef);
  const { value: sourceTemplateRef } = useAsync(async () => {
    const refCandidate =
      entity.metadata.annotations?.[SOURCE_TEMPLATE_ANNOTATION];
    let compoundRefCandidate: CompoundEntityRef | undefined;

    if (!refCandidate) {
      return undefined;
    }

    try {
      // Check for access and that this template still exists.
      const template = await catalogApi.getEntityByRef(refCandidate);
      compoundRefCandidate = parseEntityRef(refCandidate);

      return template !== undefined ? compoundRefCandidate : undefined;
    } catch {
      return undefined;
    }
  }, [catalogApi, entity]);

  return sourceTemplateRef;
};

/**
 * Returns true if the current user is a member of the entity's owner group.
 * Returns false if the entity has no owner or if the check fails.
 */
export const useIsEntityOwner = (entity: Entity) => {
  const identityApi = useApi(identityApiRef);
  const { value: isOwner } = useAsync(async () => {
    if (!entity.spec?.owner) {
      return false;
    }

    try {
      const { ownershipEntityRefs } = await identityApi.getBackstageIdentity();
      const ownerRef = (entity.spec.owner as string).includes(':')
        ? (entity.spec.owner as string)
        : `group:default/${entity.spec.owner}`;

      return ownershipEntityRefs.includes(ownerRef);
    } catch {
      return false;
    }
  }, [entity, identityApi]);

  return isOwner ?? false;
};
